// ABOUTME: Main application layout wrapper with header, footer, theme token and global CSS injection.
// ABOUTME: Theme tokens from theme_settings are injected first so custom CSS from app_settings keeps the highest priority.

import { type ReactNode, useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useThemeSettings } from '@/hooks/useThemeSettings';
import { fontsHref, generateThemeCss } from '@/lib/theme-css';

interface AppLayoutProps {
  children: ReactNode;
  hideFooter?: boolean;
}

/** Ensures a head element with the given id exists, appended in a stable order. */
const ensureTag = <T extends HTMLElement>(id: string, create: () => T): T => {
  const existing = document.getElementById(id) as T | null;
  if (existing) return existing;
  const tag = create();
  tag.id = id;
  document.head.appendChild(tag);
  return tag;
};

export const AppLayout = ({ children, hideFooter = false }: AppLayoutProps) => {
  const { settings } = useAppSettings();
  const { settings: theme } = useThemeSettings();

  useEffect(() => {
    ensureTag('theme-tokens', () => document.createElement('style')).textContent = generateThemeCss(theme);

    const href = fontsHref(theme.typography);
    if (href) {
      const link = ensureTag('theme-fonts', () => {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        return el;
      });
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    }
  }, [theme]);

  useEffect(() => {
    // Appended after the theme tokens tag so custom CSS always wins
    ensureTag('global-custom-css', () => document.createElement('style')).textContent = settings.global_css || '';
  }, [settings.global_css]);


  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      {!hideFooter && <Footer />}
    </div>
  );
};
